// vg-binding: P7.D-102
// vg-binding: P7.D-111
// vg-binding: P7.D-112
//
// apps/vendor/src/features/onboarding/wizardStore.ts
//
// Phase 7 Wave 3 Task 09 — vendor onboarding wizard state machine.
//
// 5-step shape per PLAN/index.md Wave 3:
//   1. Business Info        — business_info {business_name, ...}
//   2. KYC Documents — kyc_doc_ids[] (OPTIONAL — skippable)
//   3. Bank Details         — bank_account
//   4. Warehouse            — warehouses[]
//   5. Review               — final confirmation + submit
//
// Persistence: zustand `persist` middleware backed by localStorage so a
// page refresh resumes the user where they left off. Key
// `vendor-onboarding-draft`. Reset() clears both the in-memory state and
// the persisted slot — called after a successful submit so a fresh
// onboarding doesn't replay stale draft data.

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Shape — mirrors the API submit envelope so the page-level forms can
// dispatch directly into the store without translation layers.
// ---------------------------------------------------------------------------

export type WizardStep = 1 | 2 | 3 | 4 | 5;

export interface BusinessInfoDraft {
  business_name: string;
  legal_name: string;
  contact_email: string;
  contact_phone: string;
  country: string; // ISO-3166 alpha-2 (uppercase preferred but client trims/upcases at submit)
  timezone: string;
  language: 'en' | 'vi';
}

export interface BankAccountDraft {
  method: 'bank' | 'payoneer' | 'wise' | '';
  holder_name: string;
  account_number: string;
  bank_name: string;
  bank_country: string;
  platform_account_id: string;
}

export interface WarehouseDraft {
  name: string;
  address: string;
  country: string;
  priority: number;
  is_active: boolean;
}

export const BUSINESS_INFO_EMPTY: BusinessInfoDraft = {
  business_name: '',
  legal_name: '',
  contact_email: '',
  contact_phone: '',
  country: '',
  timezone: '',
  language: 'vi',
};

export const BANK_ACCOUNT_EMPTY: BankAccountDraft = {
  method: '',
  holder_name: '',
  account_number: '',
  bank_name: '',
  bank_country: '',
  platform_account_id: '',
};

export const WAREHOUSE_EMPTY: WarehouseDraft = {
  name: '',
  address: '',
  country: '',
  priority: 50,
  is_active: true,
};

// ---------------------------------------------------------------------------
// Step labels — bilingual EN+VI per StepProgress component (Task 09 §
// "5 dot + label EN+VI").
// ---------------------------------------------------------------------------

export interface StepLabel {
  step: WizardStep;
  /** Stable i18n key suffix under `progress.steps.` in the onboarding ns. */
  key: string;
}

export const WIZARD_STEP_LABELS: readonly StepLabel[] = [
  { step: 1, key: 'businessInfo' },
  { step: 2, key: 'kyc' },
  { step: 3, key: 'bank' },
  { step: 4, key: 'warehouse' },
  { step: 5, key: 'review' },
] as const;

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

export interface WizardState {
  current_step: WizardStep;

  business_info: BusinessInfoDraft;
  kyc_doc_ids: string[];
  bank_account: BankAccountDraft;
  warehouses: WarehouseDraft[];

  /** Idempotency key persisted across retries (generated once at first submit). */
  submit_idempotency_key: string | null;

  // ---- actions ----------------------------------------------------------
  setStep: (step: WizardStep) => void;
  goNext: () => void;
  goBack: () => void;
  setBusinessInfo: (patch: Partial<BusinessInfoDraft>) => void;
  setBankAccount: (patch: Partial<BankAccountDraft>) => void;

  addKycDoc: (doc_id: string) => void;
  removeKycDoc: (doc_id: string) => void;
  /**
   * Replace the whole id set.
   *
   * dbg-20260904 — `addKycDoc` can only grow the set, and this store is
   * localStorage-persisted with no reset anywhere in the app, so ids the server
   * no longer considers current (superseded re-uploads, deleted docs) stayed in
   * it forever and inflated the "Đã tải lên: N tài liệu" count. Reconciling
   * needs a setter that can also REMOVE.
   */
  setKycDocIds: (doc_ids: string[]) => void;

  addWarehouse: (wh: WarehouseDraft) => void;
  updateWarehouseAt: (index: number, patch: Partial<WarehouseDraft>) => void;
  removeWarehouseAt: (index: number) => void;

  getOrCreateIdempotencyKey: () => string;
  reset: () => void;

  // ---- selectors --------------------------------------------------------
  isStepValid: (step: WizardStep) => boolean;
}

// ---------------------------------------------------------------------------
// Validators — each step's "ready to advance" check. Used by isStepValid
// + the WizardLayout footer to disable the Next button.
// ---------------------------------------------------------------------------

function validateBusinessInfo(bi: BusinessInfoDraft): boolean {
  return (
    bi.business_name.trim().length > 0 &&
    bi.legal_name.trim().length > 0 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(bi.contact_email) &&
    bi.contact_phone.trim().length >= 3 &&
    bi.country.trim().length === 2 &&
    bi.timezone.trim().length > 0
  );
}

function validateBankAccount(bank: BankAccountDraft): boolean {
  if (bank.method === '') return false;
  if (bank.holder_name.trim().length === 0) return false;
  if (bank.account_number.trim().length === 0) return false;
  if (bank.method === 'bank' && bank.bank_name.trim().length === 0) return false;
  return true;
}

function validateWarehouse(w: WarehouseDraft): boolean {
  return (
    w.name.trim().length > 0 &&
    w.address.trim().length > 0 &&
    w.country.trim().length === 2 &&
    Number.isInteger(w.priority) &&
    w.priority >= 1 &&
    w.priority <= 99
  );
}

// ---------------------------------------------------------------------------
// Idempotency-key generator — same shape as the merchant store helper.
// ---------------------------------------------------------------------------

function newIdemKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `vendor-onb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useVendorWizardStore = create<WizardState>()(
  persist(
    (set, get) => ({
      current_step: 1,
      business_info: { ...BUSINESS_INFO_EMPTY },
      kyc_doc_ids: [],
      bank_account: { ...BANK_ACCOUNT_EMPTY },
      warehouses: [],
      submit_idempotency_key: null,

      setStep: (step) => set({ current_step: step }),
      goNext: () =>
        set((s) => ({
          current_step: (Math.min(5, s.current_step + 1) as WizardStep),
        })),
      goBack: () =>
        set((s) => ({
          current_step: (Math.max(1, s.current_step - 1) as WizardStep),
        })),
      setBusinessInfo: (patch) =>
        set((s) => ({ business_info: { ...s.business_info, ...patch } })),
      setBankAccount: (patch) =>
        set((s) => ({ bank_account: { ...s.bank_account, ...patch } })),

      addKycDoc: (doc_id) =>
        set((s) => ({
          kyc_doc_ids: s.kyc_doc_ids.includes(doc_id)
            ? s.kyc_doc_ids
            : [...s.kyc_doc_ids, doc_id],
        })),
      removeKycDoc: (doc_id) =>
        set((s) => ({
          kyc_doc_ids: s.kyc_doc_ids.filter((id) => id !== doc_id),
        })),
      setKycDocIds: (doc_ids) =>
        set((s) => {
          const next = [...new Set(doc_ids)];
          // Bail when nothing changed: this runs from a reconcile effect, and
          // handing back a fresh array every time would re-trigger any consumer
          // that depends on the reference.
          if (
            next.length === s.kyc_doc_ids.length &&
            next.every((id, i) => s.kyc_doc_ids[i] === id)
          ) {
            return s;
          }
          return { kyc_doc_ids: next };
        }),

      addWarehouse: (wh) =>
        set((s) => ({ warehouses: [...s.warehouses, wh] })),
      updateWarehouseAt: (index, patch) =>
        set((s) => {
          const next = s.warehouses.slice();
          const existing = next[index];
          if (!existing) return s;
          next[index] = { ...existing, ...patch };
          return { warehouses: next };
        }),
      removeWarehouseAt: (index) =>
        set((s) => {
          const next = s.warehouses.slice();
          next.splice(index, 1);
          return { warehouses: next };
        }),

      getOrCreateIdempotencyKey: () => {
        const existing = get().submit_idempotency_key;
        if (typeof existing === 'string' && existing.length > 0) {
          return existing;
        }
        const key = newIdemKey();
        set({ submit_idempotency_key: key });
        return key;
      },

      reset: () =>
        set({
          current_step: 1,
          business_info: { ...BUSINESS_INFO_EMPTY },
          kyc_doc_ids: [],
          bank_account: { ...BANK_ACCOUNT_EMPTY },
          warehouses: [],
          submit_idempotency_key: null,
        }),

      isStepValid: (step) => {
        const s = get();
        switch (step) {
          case 1:
            return validateBusinessInfo(s.business_info);
          case 2:
            // KYC docs are OPTIONAL — always pass; Skip button
            // is wired separately in WizardLayout.
            return true;
          case 3:
            return validateBankAccount(s.bank_account);
          case 4:
            return s.warehouses.length >= 1 && s.warehouses.every(validateWarehouse);
          case 5:
            // Review step needs every earlier step satisfied. Recurse via
            // the same selectors to keep validation in one place.
            return (
              validateBusinessInfo(s.business_info) &&
              validateBankAccount(s.bank_account) &&
              s.warehouses.length >= 1 &&
              s.warehouses.every(validateWarehouse)
            );
        }
      },
    }),
    {
      name: 'vendor-onboarding-draft',
      storage: createJSONStorage(() =>
        typeof window === 'undefined'
          ? // SSR / test fallback — no-op storage so reads return null
            {
              getItem: () => null,
              setItem: () => undefined,
              removeItem: () => undefined,
            }
          : window.localStorage,
      ),
    },
  ),
);

// ---------------------------------------------------------------------------
// Test-only export
// ---------------------------------------------------------------------------

export const _internals = {
  validateBusinessInfo,
  validateBankAccount,
  validateWarehouse,
};
